import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Routine Builder Block Component
 *
 * Handles bundle builder function for routine builder block,
 */
class RoutineBuilderBlock extends Component {
  constructor() {
    super();
    /** @type {HTMLInputElement | null} */
    this.bundleIdInput = this.querySelector('.js-bundle-id');
    /** @type {NodeListOf<RoutineStepBlock>} */
    this.steps = this.querySelectorAll('routine-step-block');
    /** @type {HTMLButtonElement | null} */
    this.atcButton = this.querySelector('.add-to-cart-button');
    /** @type {HTMLElement | null} */
    this.price = this.querySelector('.routine-subtotal-block .routine-subtotal__content > product-price span.price');
    /** @type {HTMLElement | null} */
    this.selectedProductsContainer = this.querySelector('.routine-subtotal__selected-products');
    this.quantity = 1;

    const localeFromDataset = this.dataset.locale || "de-DE";
    const currencyFromDataset = this.dataset.currency || 'EUR';

    this.priceFormatter = new Intl.NumberFormat(localeFromDataset, {
      style: 'currency',
      currency: currencyFromDataset,
    });

    this.discountText = this.dataset.discountText || "Du sparst";
    this.freePriceText = this.dataset.freePriceText || "Kostenlos";
  }

  get parentVariantId() {
    return "gid://shopify/ProductVariant/" + this.dataset.parentVariantId;
  }

  get parentProductId() {
    return this.dataset.parentProductId;
  }

  get bundleName() {
    return this.dataset.bundleName;
  }

  get bundleId() {
    return this.variantList.reduce((acc, id) => acc + id, '');
  }

  get discountPercentage() {
    const discount = this.dataset.discountPercentage;
    return discount ? parseInt(discount, 10) : 0;
  }

  get discountType() {
    return this.dataset.discountType || '';
  }

  get isPercentageDiscount() {
    return this.discountType === 'percentage' && this.discountPercentage > 0;
  }

  get isFreeProductDiscount() {
    return this.discountType === 'free-last-step';
  }

  get freeProductStep() {
    if (!this.isFreeProductDiscount || this.steps.length === 0) return null;

    return this.steps[this.steps.length - 1] || null;
  }

  /**
   * The cart transform makes exactly one line free per bundle, even when several
   * lines carry the free flag. So only the first product selected in the last step
   * is treated as free, both in the displayed total and in the flagged cart line.
   */
  get freeProductVariantId() {
    const freeStep = this.freeProductStep;
    const firstSelected = freeStep ? freeStep.selectedProducts[0] : null;
    if (!firstSelected) return null;

    const parsedId = parseInt(firstSelected, 10);
    return Number.isNaN(parsedId) ? null : parsedId;
  }

  get freeProductBasePrice() {
    const freeStep = this.freeProductStep;
    if (!freeStep) return 0;

    const freePrice = this.parsePrice(freeStep.currentPrices[0]);
    return freePrice || 0;
  }

  get totalDiscountAmount() {
    if (this.totalPrice === 0) return 0;

    if (this.isPercentageDiscount) {
      return this.totalPrice * (this.discountPercentage / 100);
    }

    if (this.isFreeProductDiscount && this.freeProductVariantId) {
      return this.freeProductBasePrice * this.quantity;
    }

    return 0;
  }

  get isReadyForATC() {
    return this.steps.length > 0 && Array.from(this.steps).every(step => step.isComplete);
  }

  get variantList() {
    if (this.steps.length === 0) return [];
    return Array.from(this.steps).flatMap(step => step.selectedProducts).map(id => parseInt(id, 10));
  }

  get selectedVariantListElements() {
    if (this.steps.length === 0) return [];
    return Array.from(this.steps).flatMap(step => step.selectedProductElements);
  }

  get totalPrice() {
    // Sum up prices of all selected variants
    const singleBundlePrice = this.steps
      ? Array.from(this.steps)
          .flatMap(step => step.currentPrices)
          .reduce((sum, price) => {
            const parsed = parseFloat(price.toString().replace(',', '.'));
            return sum + (isNaN(parsed) ? 0 : parsed);
          }, 0)
      : 0;
    return singleBundlePrice * this.quantity;
  }

  get totalPriceWithDiscount() {
    return Math.max(this.totalPrice - this.totalDiscountAmount, 0);
  }

  connectedCallback() {
    this.initializeProductCardPrices();

    if (this.atcButton) {
      // Initially disable the ATC button
      this.disableATCButton();

      // Listen for product changes bubbling up from routine-step-block
      this.addEventListener('productchange', () => {
        this.disableATCButton();
        this.updatePriceDisplay();
        this.updateSelectedProductsDisplay();
      });

      this.atcButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleAddToCart();
      });
    }

    this.addEventListener('quantity-selector:update', (e) => {
      /** @type {HTMLInputElement | null} */
      const target = e.target instanceof HTMLInputElement ? e.target : null;
      if(target == null) return;
      const qty = parseInt(target.value, 10) || 1; // read value safely
      this.quantity = qty;
      this.updatePriceDisplay(); // recalc total price if needed
    });
  }

  initializeProductCardPrices() {
    const noSteps = !this.steps || this.steps.length === 0;
    const noDiscount = !this.isPercentageDiscount && !this.isFreeProductDiscount;

    if (noSteps || noDiscount) return;
    
    for (const step of this.steps) {
      // @type {NodeListOf<HTMLElement>}
      const productPrices = step.querySelectorAll('.product-grid__item product-price.text-block');
      productPrices.forEach(productPrice => {
        if (!(productPrice instanceof HTMLElement)) return;
        const priceContainer = productPrice.querySelector('.price-container');
        if (!priceContainer) return;

        // Only one line per bundle is made free by the cart transform, so cards are only
        // labelled "free" while the free step is limited to a single product.
        const innerHTML = this.isPercentageDiscount
          ? this.discountPriceInnerHTML(productPrice)
          : step === this.freeProductStep && step.maxProducts === 1
            ? this.freePriceInnerHTML(productPrice)
            : null;
        if(!innerHTML) return;

        priceContainer.innerHTML = innerHTML;
      });
    }
  }

  /**
   * Generates an HTML template string displaying the original price, discounted price,
   * and unit price per 100ml for a given product, applying the current discount percentage.
   *
   * @param {HTMLElement} productPrice - The DOM element containing price data attributes and child elements.
   * @returns {string|undefined} The HTML string for the discounted price template, or undefined if required data is missing.
   */
  discountPriceInnerHTML(productPrice) {
    const currentPrice = productPrice.dataset.price;
    if (!currentPrice) return;

    const compareAtPrice = (parseInt(currentPrice) / 100); // Assuming original price is stored in cents
    const discountedPrice = (parseInt(currentPrice) / 100) * (1 - this.discountPercentage / 100);
    const pricePer100mlEl = productPrice.querySelector('.price-per-100-ml');
    const pricePer100ml = pricePer100mlEl && pricePer100mlEl instanceof HTMLElement ? pricePer100mlEl.dataset.price : null;
    const parsedPricePer100ml = this.parsePrice(pricePer100ml);
    
    if (!parsedPricePer100ml) return;

    const unitPrice = parsedPricePer100ml * (1 - this.discountPercentage / 100);

    return `
      <span role="group">
        <span class="visually-hidden">Angebotspreis&nbsp;</span>
        <span class="price price-sale">
          ${this.priceFormatter.format(discountedPrice)}
        </span>
      </span>
      <span role="group">
        <span class="visually-hidden">Normaler Preis&nbsp;</span>
        <span class="compare-at-price">${this.priceFormatter.format(compareAtPrice)}</span>
      </span>
      <small class="unit-price price-per-100-ml">
        <span class="visually-hidden">Grundpreis</span>
          (${this.priceFormatter.format(unitPrice)} / 100
        ml)</small>
    `;
  }

  /**
   * Generates an HTML template string for free-last-step pricing.
   *
   * @param {HTMLElement} productPrice - The DOM element containing price data attributes and child elements.
   * @returns {string|undefined} The HTML string for the free price template, or undefined if required data is missing.
   */
  freePriceInnerHTML(productPrice) {
    const currentPrice = productPrice.dataset.price;
    if (!currentPrice) return;

    const compareAtPrice = parseInt(currentPrice, 10) / 100;

    return `
      <span role="group">
        <span class="visually-hidden">Angebotspreis&nbsp;</span>
        <span class="price price-sale">
          ${this.freePriceText}
        </span>
      </span>
    `;
  }

  updatePriceDisplay() {
    if (!this.price) return;
    
    this.price.textContent = this.priceFormatter.format(this.totalPriceWithDiscount);

    /** @type {HTMLElement | null} */
    // Remove any existing strike or discount elements
    const strikeEl = this.querySelector('.routine-subtotal-block .routine-subtotal__content > product-price span.compare-at-price');
    if (strikeEl) strikeEl.remove();
    const savedEl = this.querySelector('.routine-subtotal-block .routine-subtotal__content > product-price span.saved-amount');
    if (savedEl) savedEl.remove();

    if (this.totalDiscountAmount === 0 || this.totalPrice === 0) {
      this.price.classList.remove('has-discount');
      return;
    }

    this.price.classList.add('has-discount');

    // Strike-through original price
    const newStrikeEl = document.createElement('span');
    newStrikeEl.classList.add('compare-at-price');
    newStrikeEl.textContent = this.priceFormatter.format(this.totalPrice);
    this.price.after(newStrikeEl);

    // Discount text "(xx% gespart)"
    const newSavedEl = document.createElement('span');
    newSavedEl.classList.add('saved-amount');
    if (this.isPercentageDiscount) {
      newSavedEl.textContent = `${this.discountText} ${this.discountPercentage}%`;
    } else {
      newSavedEl.textContent = `${this.discountText} ${this.priceFormatter.format(this.totalDiscountAmount)}`;
    }
    newStrikeEl.after(newSavedEl);
  }

  updateSelectedProductsDisplay() {
    if (!this.selectedProductsContainer) return;

    // Clear existing content
    this.selectedProductsContainer.innerHTML = '';

    // Add currently selected products
    this.selectedVariantListElements.forEach(element => {
      if (!element || !this.selectedProductsContainer) return;

      const clone = element.cloneNode(true);
      this.selectedProductsContainer.appendChild(clone);
    });
  }

  disableATCButton() {
    if(!this.atcButton) return;
    this.atcButton.disabled = !this.isReadyForATC;
  }

  /**
   * Parses a price string and returns its numeric value.
   * @param {string | null | undefined} priceStr
   * @returns {number | undefined}
   */
  parsePrice(priceStr) {
    if (!priceStr) return;

    // Remove spaces, currency symbols, and anything not a digit, comma, dot, or minus
    let normalized = priceStr.replace(/[^\d,.\-]/g, '').trim();

    // Replace comma with dot if comma looks like the decimal separator
    const commaCount = (normalized.match(/,/g) || []).length;
    const dotCount = (normalized.match(/\./g) || []).length;

    if (commaCount === 1 && dotCount === 0) {
      normalized = normalized.replace(',', '.');
    } else if (commaCount > 0 && dotCount > 0) {
      // If both exist, assume the last one is decimal
      const lastComma = normalized.lastIndexOf(',');
      const lastDot = normalized.lastIndexOf('.');
      if (lastComma > lastDot) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
    }

    const value = parseFloat(normalized);
    if (Number.isNaN(value)) return;

    return value;
  }

  async handleAddToCart() {
    if (this.variantList.length == 0 || !this.bundleId) return;

    const freeVariantId = this.freeProductVariantId;

    const items = this.variantList.map((id) => ({
      id,
      quantity: this.quantity,
      properties: {
        _routine_bundle_id: this.bundleId,
        _routine_bundle_name: this.bundleName,
        _routine_bundle_parent_id: this.parentVariantId,
        ...(this.discountType ? {
          _bundle_discount_type: this.discountType
        } : {}),
        ...(this.isPercentageDiscount ? {
          _bundle_discount: this.discountPercentage
        } : {}),
        ...(this.isFreeProductDiscount && freeVariantId === id ? {
          _bundle_free_product: 'true'
        } : {})
      },
    }));

    const fetchCfg = fetchConfig('json', { body: JSON.stringify({ items }) });

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      if (!res.ok) {
        const errText = await res.json();
        // this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', res.message, res.description, res.errors));
        throw new Error(`Add to cart failed: ${res.status} ${errText}`);
      }

      const json = await res.json();
      console.log('✅ Added to cart:', json);
      this.dispatchEvent(
        new CartAddEvent({}, 'routine-form', {
          source: 'product-form-component',
          itemCount: 1,
          productId: this.parentProductId,
          // sections: res.sections,
        })
      );
      return json;
    } catch (err) {
      console.error('❌ addToCart error:', err);
    } finally {
      this.classList.remove('loading');
    }
  }
}

if (!customElements.get('routine-builder-block')) {
  customElements.define('routine-builder-block', RoutineBuilderBlock);
}

/**
 * Routine Step Block Component
 *
 * Handles bundle builder function for routine step block,
 */
class RoutineStepBlock extends Component {
  constructor() {
    super();
    this.productOptions = this.querySelectorAll('.product-grid__item');
    /** @type {HTMLElement | null} */
    this.counter = this.querySelector('.routine-step-block__counter');
    this.selectedCountText = this.dataset.selectedCountText || '';
    this.selectedCountCompleteText = this.dataset.selectedCountCompleteText || this.selectedCountText;
  }

  /** Number of products the customer has to select before this step counts as complete. */
  get minProducts() {
    const min = parseInt(this.dataset.minProducts || '1', 10);
    return Number.isNaN(min) || min < 1 ? 1 : min;
  }

  /** Upper bound of selectable products. `0` in the block settings means no limit; never below the minimum. */
  get maxProducts() {
    const max = parseInt(this.dataset.maxProducts || '1', 10);
    if (Number.isNaN(max) || max === 0) return Infinity;
    return Math.max(max, this.minProducts);
  }

  /** @returns {HTMLElement[]} selected cards in DOM order */
  get selectedProductElements() {
    return Array.from(this.productOptions).filter(
      (option) => option instanceof HTMLElement && option.classList.contains('is-selected')
    ).map((option) => /** @type {HTMLElement} */ (option));
  }

  /** @returns {string[]} variant ids of the selected cards */
  get selectedProducts() {
    return this.selectedProductElements
      .map((option) => option.dataset.variantId)
      .filter((id) => id != null && id !== '')
      .map((id) => /** @type {string} */ (id));
  }

  /** @returns {string[]} prices of the selected cards, as rendered by Liquid */
  get currentPrices() {
    return this.selectedProductElements
      .map((option) => option.dataset.currentPrice)
      .filter((price) => price != null && price !== '')
      .map((price) => /** @type {string} */ (price));
  }

  get selectedCount() {
    return this.selectedProductElements.length;
  }

  get isComplete() {
    return this.selectedCount >= this.minProducts;
  }

  get isFull() {
    return this.selectedCount >= this.maxProducts;
  }

  get productGridItems() {
    if (this.productOptions.length === 0) return [];
    return this.querySelectorAll('.product-grid__item');
  }

  get productHeader() {
    return this.querySelector('.routine-step-block__product-header');
  }

  get isMobile() {
    return window.innerWidth <= 749; // adjust breakpoint as needed
  }

  get accordionCustom() {
    return this.querySelector('accordion-custom');
  }

  get dropdown() {
    return this.querySelector('.routine-step-block__details');
  }

  connectedCallback() {
    this.productOptions.forEach((option /** @type {HTMLElement} */) => {
      option.addEventListener('click', () => {
        this.handleSelectedProduct(option);
      });
    });
    this.updateSelectionState();
  }

  /**
   * Handles selecting a product option.
   * @param {Element} productOption
   * @returns {void}
   */
  handleSelectedProduct(productOption) {
    if (productOption.classList.contains('is-selected')) {
      productOption.classList.remove('is-selected');
      this.updateSelectionState();

      // Let the parent RoutineBuilderBlock know something changed
      this.dispatchEvent(new CustomEvent('productchange', { bubbles: true }));
      return;
    }
    
    if (
      this.isFull ||
      productOption.classList.contains('is-unavailable')
    ) return;

    productOption.classList.add('is-selected');
    this.updateSelectionState();

    // Let the parent RoutineBuilderBlock know something changed
    this.dispatchEvent(new CustomEvent('productchange', { bubbles: true }));

    // Close dropdown automatically on mobile once no further product can be picked here.
    // Steps without an upper limit stay open; the customer moves on by themselves.
    if (this.isMobile && this.isFull) {
      this.closeDropdown();
    }
  }

  updateSelectionState() {
    this.classList.toggle('is-complete', this.isComplete);
    this.classList.toggle('is-full', this.isFull);
    this.updateCounter();
    this.updateProductHeader();
  }

  updateCounter() {
    if (!this.counter || !this.selectedCountText) return;
    // "3 of 2 selected" reads wrong once the minimum is exceeded, so switch to the plain count then
    const template = this.selectedCount > this.minProducts ? this.selectedCountCompleteText : this.selectedCountText;
    this.counter.textContent = template.replace('[count]', String(this.selectedCount));
  }

  updateProductHeader() {
    if (!this.productHeader) return;

    if (this.selectedCount === 0) {
      this.productHeader.classList.add("hidden");
      return;
    }
    
    // Update the product title in the header based on the selected products
    const titleEl = this.productHeader.querySelector('.routine-step-block__product-title');
    if (titleEl) {
      titleEl.textContent = this.findSelectedProductTitles().join(', ');
    }

    this.productHeader.classList.remove("hidden");
  }

  findSelectedProductTitles() {
    // Cards configured in the theme editor may render the title via the product-card link
    // instead of the product-title block, so fall back to that link.
    return this.selectedProductElements
      .map((option) => {
        const titleEl =
          option.querySelector('[ref="productTitleLink"] p') || option.querySelector('.product-card__link');
        return titleEl?.textContent?.trim() || '';
      })
      .filter((title) => title !== '');
  }

  closeDropdown(delay = 250) {
    setTimeout(() => {
      if (!this.dropdown) return;
      this.dropdown.removeAttribute('open');

      // All routine-step-blocks
      const allBlocks = Array.from(document.querySelectorAll('routine-step-block'));
      const currentIndex = allBlocks.indexOf(this);

      // Remove any existing open-by-default attributes from other blocks to prevent conflicts
      allBlocks.forEach(block => {
        const accordion = /** @type {RoutineStepBlock} */ (block).accordionCustom;
        if (!accordion) return;
        accordion.removeAttribute('open-by-default-on-mobile');
      });

      // Helper to find the next block matching a condition
      /**
       * @param {number} startIndex
       * @param {{ (block: any): boolean; (block: any): boolean; (arg0: Element | undefined): any; }} condition
       */
      function findNextBlock(startIndex, condition) {
        for (let i = startIndex; i < allBlocks.length; i++) {
          if (condition(allBlocks[i])) return allBlocks[i];
        }
        return null;
      }

      // First, check forward for blocks that are not complete yet
      let nextBlock = findNextBlock(currentIndex + 1, block => !block.isComplete);

      // If nothing forward, check from start up to current index
      if (!nextBlock) {
        nextBlock = findNextBlock(0, block => !block.isComplete && allBlocks.indexOf(block) < currentIndex);
      }

      const subRoutineTotal = document.querySelector('.routine-subtotal-block');
      console.log("subRoutineTotal", subRoutineTotal)

      if (nextBlock) {
        const nextAccordionCustom = /** @type {RoutineStepBlock} */ (nextBlock).accordionCustom;
        const nextDropdown = /** @type {RoutineStepBlock} */ (nextBlock).dropdown;

        if (nextAccordionCustom) nextAccordionCustom.setAttribute('open-by-default-on-mobile', '');
        if (nextDropdown) nextDropdown.setAttribute('open', '');

        this.scrollToView(/** @type {RoutineStepBlock} */ (nextBlock), 200, delay);
      } else if(subRoutineTotal) {
        this.scrollToView(/** @type {HTMLElement} */ (subRoutineTotal), 200, delay);
      }
    }, delay);
  }

  /**
   * @param {HTMLElement} element
   * @param {number} offset
   * @param {number} delay
   * @returns {void}
   */
  scrollToView(element, offset, delay) {
    setTimeout(() => {
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, delay);
  }
}

if (!customElements.get('routine-step-block')) {
  customElements.define('routine-step-block', RoutineStepBlock);
}

